import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/auth";
import axios from "axios";
import { Icon } from "@/components/kit";
import { toast } from "sonner";

const DURATIONS = ["15 min", "30 min", "60 min"];
const TYPES = ["Financial discussion", "Portfolio review", "SIP discussion", "New investor consultation", "Digital consultation"];
const TIMES = ["10:00 AM", "11:00 AM", "12:00 PM", "2:00 PM", "3:30 PM", "4:30 PM", "5:30 PM"];

export default function BookPublic() {
  const { slug } = useParams();
  const [site, setSite] = useState(undefined);
  const [duration, setDuration] = useState("30 min");
  const [type, setType] = useState(TYPES[0]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ client_name: "", client_email: "", note: "" });
  const [done, setDone] = useState(null);

  useEffect(() => { axios.get(`${API}/site/${slug}`).then((r) => setSite(r.data)).catch(() => setSite(null)); }, [slug]);

  const submit = async () => {
    if (!form.client_name || !form.client_email || !date || !time) return toast.error("Please fill all fields & pick a slot");
    try {
      await axios.post(`${API}/appointments`, { slug, ...form, date, time, duration, meeting_type: type });
      setDone({ date, time, duration, type });
    } catch { toast.error("Could not book. Try again."); }
  };

  const gcalLink = () => {
    const title = encodeURIComponent(`Meeting with ${site?.name} (MFDX)`);
    const details = encodeURIComponent(`${type} · ${duration}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}`;
  };

  if (site === undefined) return <div className="h-screen grid place-items-center text-gray-400 font-head">Loading…</div>;
  if (site === null) return <div className="h-screen grid place-items-center text-center"><div><Icon name="CalendarX" size={40} className="mx-auto text-gray-300 mb-3" /><h1 className="font-head text-2xl font-bold">Booking page unavailable</h1><Link to="/" className="text-blue-600 font-semibold mt-3 inline-block">← MFDX</Link></div></div>;

  if (done) return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAFA] p-6">
      <div className="card-mfx p-8 max-w-md text-center fade-up">
        <div className="w-14 h-14 rounded-2xl grad-lime-cyan grid place-items-center mx-auto mb-4"><Icon name="CheckCircle2" size={28} className="text-white" /></div>
        <h1 className="font-head text-2xl font-bold">Booking confirmed!</h1>
        <p className="text-gray-500 mt-2">Your {done.duration} {done.type.toLowerCase()} with {site.name} is set for <b>{done.date}</b> at <b>{done.time}</b>.</p>
        <a href={gcalLink()} target="_blank" rel="noreferrer" data-testid="gcal-link" className="inline-flex items-center gap-2 mt-6 grad-blue-violet text-white rounded-xl px-5 py-3 font-semibold hover-lift"><Icon name="Calendar" size={18} /> Add to Google Calendar</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="grad-blue-violet text-white p-8 md:p-12">
        <Link to="/" className="text-white/80 text-sm">MFDX</Link>
        <h1 className="font-head text-3xl md:text-4xl font-extrabold mt-3">Book a meeting with {site.name}</h1>
        <p className="text-white/85 mt-1">{site.location} · Mutual Fund Distributor</p>
      </div>
      <div className="max-w-3xl mx-auto p-6 -mt-6">
        <div className="card-mfx p-6 md:p-8 space-y-6">
          <div>
            <label className="text-sm font-semibold">Duration</label>
            <div className="flex gap-2 mt-2">{DURATIONS.map((d) => <button key={d} onClick={() => setDuration(d)} data-testid={`book-duration-${d.replace(" ", "")}`} className={`px-4 py-2 rounded-xl text-sm font-medium border ${duration === d ? "grad-blue-violet text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{d}</button>)}</div>
          </div>
          <div>
            <label className="text-sm font-semibold">Meeting type</label>
            <div className="flex flex-wrap gap-2 mt-2">{TYPES.map((t) => <button key={t} onClick={() => setType(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${type === t ? "grad-blue-violet text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{t}</button>)}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-semibold">Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date" className={inp} /></div>
            <div><label className="text-sm font-semibold">Time slot</label>
              <div className="flex flex-wrap gap-2 mt-2">{TIMES.map((t) => <button key={t} onClick={() => setTime(t)} data-testid="book-time-slot" className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${time === t ? "bg-blue-600 text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{t}</button>)}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-semibold">Your name</label><input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="book-name" className={inp} placeholder="Your name" /></div>
            <div><label className="text-sm font-semibold">Email</label><input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="book-email" className={inp} placeholder="you@example.com" /></div>
          </div>
          <div><label className="text-sm font-semibold">Note (optional)</label><textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inp} placeholder="What would you like to discuss?" /></div>
          <button onClick={submit} data-testid="book-confirm-btn" className="w-full py-3.5 rounded-xl grad-blue-violet text-white font-semibold hover-lift flex items-center justify-center gap-2"><Icon name="CalendarPlus" size={18} /> Confirm booking</button>
          <p className="text-[11px] text-gray-400 text-center">Mutual fund investments are subject to market risks. This is a scheduling request, not investment advice.</p>
        </div>
      </div>
    </div>
  );
}
const inp = "mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-blue-400";
